import axios, { AxiosInstance } from "axios";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";

export const calendarClientInstance: AxiosInstance = axios.create({
  baseURL: "https://data-core.teampbs.com/calendar/api",
  timeout: 120000,
});

// Helper function to check if error requires interaction
const isInteractionRequiredError = (error: any): boolean => {
  return error instanceof InteractionRequiredAuthError ||
         error.errorCode === "interaction_required" ||
         error.errorCode === "consent_required" ||
         error.errorCode === "login_required" ||
         error.errorCode === "token_expired";
};

export const initializeCalendarService = (msalApp: PublicClientApplication) => {
  calendarClientInstance.interceptors.request.use(async function (config) {
    if (!msalApp) throw new Error("MSAL not initialized");
    
    const activeAccount = msalApp.getActiveAccount();
    const accounts = msalApp.getAllAccounts();
    
    // Handle unauthenticated state
    if (!activeAccount && accounts.length === 0) {
      console.warn("No active MSAL account found for Calendar service. User is not authenticated.");
      
      // Check if authentication is already in progress
      try {
        const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
        if (activeRequest) {
          console.info("Authentication already in progress, skipping Calendar API call");
          return Promise.reject(new Error("Authentication in progress - Calendar API call will retry after login"));
        }
      } catch (checkError) {
        // Ignore errors checking interaction state
      }
      
      // Don't initiate auth flow here - let the app-level authentication handle it
      console.info("Deferring Calendar authentication to app-level flow");
      return Promise.reject(new Error("Authentication required - handled by app level"));
    }
    
    const tokenRequest = {
      scopes: ["api://cbbfd2ee-1c1b-46c0-b6b6-8174d148ad23/access_as_user"],
      account: activeAccount || accounts[0],
    };
    
    try {
      const tokenResponse = await msalApp.acquireTokenSilent(tokenRequest);
      config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return config;
    } catch (error: any) {
      console.error("Error acquiring token silently for Calendar service:", error);
      
      // Handle specific MSAL errors that require interaction
      if (isInteractionRequiredError(error)) {
        console.info("Interactive authentication required for Calendar. Checking for ongoing interactions...");
        
        // Check if interaction is already in progress
        try {
          const activeRequest = (msalApp as any).browserStorage?.getInteractionInProgress();
          if (activeRequest) {
            console.info("Authentication interaction already in progress, skipping Calendar token request");
            return Promise.reject(new Error("Authentication in progress - Calendar token will be acquired after login"));
          }
        } catch (checkError) {
          // Ignore errors checking interaction state
        }
        
        // Don't initiate auth flow here - let the app-level authentication handle it
        console.info("Deferring Calendar interactive authentication to app-level flow");
        return Promise.reject(new Error("Interactive authentication required - handled by app level"));
      }
      
      return Promise.reject(error);
    }
  });
  
  // Add response interceptor for 401 handling
  calendarClientInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.warn("Received 401 Unauthorized response from Calendar service. Deferring to app-level authentication.");
        // Don't initiate login here - let the app handle it
      }
      return Promise.reject(error);
    }
  );
};
